import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getGlobalSearchTerm, setGlobalSearchTerm } from '../components/GlobalSearch';

/**
 * Hook to manage search highlighting on destination pages
 * - Highlights matching text for 5 seconds after navigation from global search
 * - Returns the search term and a function to clear it
 */
export const useSearchHighlight = () => {
  const location = useLocation();
  const [searchTerm, setSearchTermState] = useState('');
  const [isHighlighting, setIsHighlighting] = useState(false);

  useEffect(() => {
    // Check if we have a search highlight from navigation state
    const stateSearchTerm = location.state?.searchHighlight;
    const globalTerm = getGlobalSearchTerm();
    
    const termToUse = stateSearchTerm || globalTerm;
    
    if (termToUse) {
      setSearchTermState(termToUse);
      setIsHighlighting(true);
      
      // Clear the highlight after 5 seconds
      const timer = setTimeout(() => {
        setIsHighlighting(false);
        setSearchTermState('');
        setGlobalSearchTerm('');
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const clearHighlight = useCallback(() => {
    setIsHighlighting(false);
    setSearchTermState('');
    setGlobalSearchTerm('');
  }, []);

  return {
    searchTerm: isHighlighting ? searchTerm : '',
    isHighlighting,
    clearHighlight
  };
};

/**
 * Component to highlight text that matches the search term
 */
export const HighlightedText = ({ text, searchTerm, className = '' }) => {
  if (!searchTerm || !text) {
    return <span className={className}>{text}</span>;
  }
  
  const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi');
  const parts = String(text).split(regex);
  
  return (
    <span className={className}>
      {parts.map((part, index) => 
        part.toLowerCase() === searchTerm.toLowerCase() ? (
          <mark 
            key={index} 
            className="search-highlight bg-yellow-400/50 text-inherit rounded px-0.5"
          >
            {part}
          </mark>
        ) : (
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
};

// Helper to escape special regex characters
function escapeRegExp(string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export default useSearchHighlight;
